import type { DeliveryGroup } from '@/modules/cart/domain/Cart';
import type { LineItem } from '@/modules/cart/infrastructure/dto/HttpCartRepositoryDto';
import {
  TransactionState,
  TransactionType
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/orderV2';

import type { PaymentIconTypes } from '../../containers/payment-complete/components/purchase-payment-methods';
import type {
  IInterfaceInteractions,
  IOrder,
  IPayment,
  IPrices,
  IProcessorResponse
} from '../../interfaces/src/bff-web/billing/interfaces/orderV2';

const getSuccessInteractions = (order: IOrder) => {
  const payment = order?.payments[0];
  const transactionsSuccess = filterChargeTransactionSuccess(payment);

  const transactionIds =
    transactionsSuccess?.map((trx) => {
      return trx?.interactionId;
    }) ?? [];

  const interfaceInteractions =
    payment?.interfaceInteractions?.filter((inter) => {
      const processorResponse = inter?.processorResponse;
      if (!processorResponse) return false;

      const { cardType, status, codresp, paymentMethodId } = processorResponse;

      const isStatusAuthorized = status === 'AUTHORIZED';
      const isCodrespI000 = codresp === 'I000';
      const hasBasicInfo = cardType || paymentMethodId;

      const hasTransactionId = transactionIds.includes(inter?.interactionId);
      const isAuthorized = isStatusAuthorized || isCodrespI000;

      return hasTransactionId && (isAuthorized || hasBasicInfo);
    }) ?? [];

  return interfaceInteractions;
};

const filterChargeTransactionSuccess = (elm: IPayment) => {
  const { transactions = [] } = elm ?? [];
  const failedCharges = new Set();

  transactions.forEach((tx) => {
    if (
      tx.type === TransactionType.Charge &&
      tx.state === TransactionState.Failure
    ) {
      failedCharges.add(tx.interactionId);
    }
  });

  return transactions.filter(
    (tx) =>
      tx.type === TransactionType.Charge &&
      tx.state === TransactionState.Success &&
      !failedCharges.has(tx.interactionId)
  );
};

const extractItems = (group: DeliveryGroup, lineItems: LineItem[]) => {
  const productKeys = group?.products;
  return productKeys?.map((key) =>
    lineItems?.find((lineItem) => lineItem.variant.key === key)
  );
};

const isCencoItem = (item?: LineItem) => {
  const variant = item?.variant;
  const priceMeta = variant?.prices || ({} as IPrices);
  const { offer, paymentMethod } = priceMeta || {};

  return !offer && Boolean(paymentMethod);
};

const hasCencoPrice = (group: DeliveryGroup, lineItems: LineItem[]) => {
  const items = extractItems(group, lineItems);
  return items?.some(isCencoItem);
};

const hasPinpadPayment = (processorResponse: IProcessorResponse): boolean => {
  const key = 'pinpadResponse';
  return key in processorResponse && Boolean(processorResponse[key]);
};

const getPaymentMethodInfo = (payment: IPayment) => {
  const paymentType = payment?.paymentMethodInfo?.method ?? '';
  const cardName = payment?.paymentMethodInfo?.name?.['es-CL'] ?? '';
  const amount = String(payment?.amountPlanned?.centAmount ?? 0);

  return {
    paymentType,
    cardName,
    amount
  };
};

const getGiftCardDetails = (processorResponse: IProcessorResponse) => {
  const paymentMethodName = processorResponse?.paymentMethodName ?? '';
  const cardNumber = processorResponse?.cardDetail?.cardNumber ?? '';
  const bankCardType = processorResponse?.cardTypeName ?? '';
  const detalle = processorResponse?.detalle.find((elm) => elm);
  const amount = detalle?.monto ?? '';
  const numberAmount = String(parseInt(amount, 10));

  return {
    type: paymentMethodName ?? '',
    cardNumber: cardNumber,
    bankCardType: bankCardType,
    amount: numberAmount
  };
};

const getCencopayDetails = (
  processorResponse: IProcessorResponse,
  payment: IPayment
) => {
  const { paymentType, cardName, amount } = getPaymentMethodInfo(payment);

  const cardNumber =
    processorResponse?.authorizationInfo?.[0]?.lastDigits ?? '';
  const paymentMethodId = processorResponse?.paymentMethodId ?? '';
  const bankCardType = processorResponse?.cardTypeName ?? '';
  const paymentMethodName = processorResponse?.paymentMethodName ?? '';
  const cardVendorName = processorResponse?.cardTypeName as
    | PaymentIconTypes
    | undefined;

  return {
    type: paymentType,
    cardName: cardName,
    cardNumber: cardNumber,
    cardBankName: paymentMethodId,
    bankCardType: bankCardType || paymentMethodName,
    cardVendorName: cardVendorName,
    amount: amount
  };
};

const getWebpayDetails = (
  processorResponse: IProcessorResponse,
  payment: IPayment
) => {
  const {
    card_detail: cardDetail,
    cardTypeName,
    paymentMethodId = ''
  } = processorResponse || {};

  const cardNumber = cardDetail?.card_number ?? '';
  const bankCardType = cardTypeName ?? '';

  const { paymentType, cardName, amount } = getPaymentMethodInfo(payment);
  const isCredit = processorResponse?.paymentMethodName === 'CREDIT_CARD';
  const paymentTransactionType = isCredit ? 'Crédito' : 'Débito';

  return {
    type: paymentType,
    cardName: cardName,
    cardNumber: cardNumber,
    cardBankName: paymentMethodId,
    bankCardType: bankCardType,
    cardVendorName: 'Webpay',
    amount: amount,
    paymentTransactionType
  };
};

const getPinpadDetails = (
  processorResponse: IProcessorResponse,
  payment: IPayment
) => {
  const cardNumber = processorResponse?.pinpadResponse?.cuatroDigitos ?? '';
  const bankCardType = processorResponse?.pinpadResponse?.cardTypeName ?? '';
  const cardVendorName = processorResponse?.pinpadResponse?.cardTypeName as
    | PaymentIconTypes
    | undefined;

  const { paymentType, amount } = getPaymentMethodInfo(payment);

  const isCredit = processorResponse?.paymentMethodName === 'CREDIT_CARD';
  const isCencosudCredit =
    processorResponse?.paymentMethodName === 'CENCOSUD_CREDIT_CARD';

  let paymentTransactionType = 'Débito';
  if (isCredit) {
    paymentTransactionType = 'Crédito';
  } else if (isCencosudCredit) {
    paymentTransactionType = 'Crédito Cencosud';
  }

  return {
    type: paymentType,
    cardNumber: cardNumber,
    cardBankName: cardVendorName,
    bankCardType: bankCardType,
    cardVendorName: cardVendorName,
    amount: amount,
    paymentTransactionType
  };
};

const getDefaultPaymentDetails = (
  processorResponse: IProcessorResponse,
  payment: IPayment
) => {
  const cardNumber = processorResponse?.cardDetail?.cardNumber ?? '';
  const bankCardType = processorResponse?.cardTypeName ?? '';
  const cardVendorName = processorResponse?.cardTypeName as
    | PaymentIconTypes
    | undefined;

  const { paymentType, cardName, amount } = getPaymentMethodInfo(payment);
  const isCredit =
    processorResponse?.paymentMethodName === 'CENCOSUD_CREDIT_CARD';
  const paymentTransactionType = isCredit ? 'Crédito' : 'Débito';
  return {
    type: paymentType,
    cardName: cardName,
    cardNumber: cardNumber,
    cardBankName: cardVendorName,
    bankCardType: bankCardType,
    cardVendorName: cardVendorName,
    amount: amount,
    paymentTransactionType
  };
};

const getPaymentDetails = (
  interfaceInteractions: IInterfaceInteractions[],
  payment: IPayment
) => {
  return interfaceInteractions.map((inter) => {
    const processorResponse = inter?.processorResponse;
    if (!processorResponse) return;

    const paymentMethodName = processorResponse?.paymentMethodName ?? '';
    const paymentMethodId = processorResponse?.paymentMethodId ?? '';

    const isGiftCard = paymentMethodName === 'GIFT_CARD';
    const isWebpay = paymentMethodId === 'WEBPAY';
    const isCencopay = paymentMethodId === 'CENCOPAY';

    const isPinpad = hasPinpadPayment(processorResponse);

    if (isCencopay) {
      const cencoPayDetails = getCencopayDetails(processorResponse, payment);
      return cencoPayDetails;
    }

    if (isWebpay) {
      const webPayDetails = getWebpayDetails(processorResponse, payment);
      return webPayDetails;
    }

    if (isGiftCard) {
      const giftCardDetails = getGiftCardDetails(processorResponse);
      return giftCardDetails;
    }

    if (isPinpad) {
      const pinpadDetails = getPinpadDetails(processorResponse, payment);
      return pinpadDetails;
    }

    const defaultPaymentDetails = getDefaultPaymentDetails(
      processorResponse,
      payment
    );
    return defaultPaymentDetails;
  });
};

export {
  extractItems,
  filterChargeTransactionSuccess,
  getPaymentDetails,
  getSuccessInteractions,
  hasCencoPrice,
  isCencoItem
};
